import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { catchError, map, of } from 'rxjs';
import { Article } from 'src/app/interfaces/article';
import { GET_ARTICLE_BY_ID_QUERY } from 'src/app/services/article.config';
import { SeoService } from 'src/app/services/seo.service';
import {
  discoverArticlePath,
  parseDiscoverArticleRouteId,
  unwrapArticlePayload,
} from 'src/app/utils/discover-article-url';
import { applyArticlePageSeo } from './article-page-seo.util';

/**
 * Loads published article metadata and applies SEO before the SSR render.
 * In the browser the route activates immediately so the viewer can show a
 * loading state; the page loads the document itself.
 */
export const articlePageResolver: ResolveFn<Article | null> = (route) => {
  const parsed = parseDiscoverArticleRouteId(route.paramMap.get('articleId'));

  if (!parsed) {
    return new RedirectCommand(inject(Router).parseUrl('/discover'));
  }

  if (isPlatformBrowser(inject(PLATFORM_ID))) {
    return of(null);
  }

  const apollo = inject(Apollo);
  const seoService = inject(SeoService);
  const router = inject(Router);

  return apollo
    .query<{ article: Article }>({
      query: GET_ARTICLE_BY_ID_QUERY,
      variables: { id: parsed.id },
      fetchPolicy: 'network-only',
    })
    .pipe(
      map((result) => {
        const article = unwrapArticlePayload(result.data?.article);
        if (!article) {
          throw new Error(`Article ${parsed.id} not found`);
        }
        applyArticlePageSeo(seoService, article);

        const canonical = discoverArticlePath(article);
        const current = `/discover/${parsed.id}`;
        if (canonical.startsWith('/discover/') && canonical !== current) {
          const tree = router.parseUrl(canonical);
          tree.queryParams = route.queryParams;
          return new RedirectCommand(tree);
        }

        return article;
      }),
      catchError(() => of(null)),
    );
};
